import React, { useState, useRef } from 'react';
import { useChatStore } from 'src/store/chat';
import { useMutation } from '@tanstack/react-query';
import { InputProps } from '@copilotkit/react-ui';
import { ArrowUp, Loader2 } from 'lucide-react';
import { useCopilotChat } from '@copilotkit/react-core';
import { MessageRole, TextMessage } from '@copilotkit/runtime-client-gql';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { WelcomeService } from 'src/services/welcome.service';
import { useAI } from 'src/hooks/useAI';
import { useLocation } from 'react-router-dom';

import { cn } from '@utils/index';

export const SidebarInput = ({ onSend, inProgress }: InputProps) => {
  const location = useLocation();
  const isThanks = location.pathname === '/thanks';
  const setMessages = useChatStore((state) => state.setMessages);
  const setSessionId = useChatStore((state) => state.setSessionId);
  const setSuggestedQuestions = useChatStore(
    (state) => state.setSuggestedQuestions
  );
  const [showFade, setShowFade] = useState(false);
  const currentStep = useOnboardingStore((state) => state.currentStep);
  const [text, setText] = useState('');
  const { appendMessage, runChatCompletion } = useCopilotChat();
  const { sendMessage: sendInsuranceMessage } = useAI();

  const { mutateAsync: sendMessage, isPending } = useMutation({
    mutationFn: async (message: string) => {
      const response = await sendInsuranceMessage(message);
      console.log('sendMessage response', response);
      return response;
    },
    onSuccess: async (data) => {
      if (data) {
        if (data.session_id) {
          setSessionId(data.session_id);
        }
      }
    },
    onError: (error) => {
      console.error('Message failed to send:', error);
    },
  });

  const { mutateAsync: getVideoAgent } = useMutation({
    mutationFn: WelcomeService.getWelcomeVideoAgent,
    onSuccess: async (data) => {
      const { messages, session_id, suggested_questions } = data;
      const message = messages.at(-1)?.content || '';

      const assistantMessage = new TextMessage({
        content: message,
        role: MessageRole.Assistant,
      });

      await appendMessage(assistantMessage, {
        followUp: false,
      });

      setSessionId(session_id);
      setSuggestedQuestions(suggested_questions);
    },
    onError: (error) => {
      console.error('Video agent failed:', error);
    },
  });

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const resetTextareaHeight = () => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = 'auto';
    textareaRef.current.style.minHeight = '60px';
    textareaRef.current.style.overflowY = 'hidden';
  };

  const handleSubmit = async (value: string) => {
    const userMessage = new TextMessage({
      content: value,
      role: MessageRole.User,
    });

    // Always append to CopilotKit first
    appendMessage(userMessage, { followUp: false });
    console.log('handleSubmit', value);
    // Then send to our backend
    await sendMessage(value);

    // Restore focus to textarea after submitting
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, 0);
  };
 
  const handleSend = async () => {
    const message = text.trim();
    console.log('handleSend', message);
    if (message === '') return;
    setText('');
    resetTextareaHeight();
    await handleSubmit(message);
    setShowFade(false);

    // Restore focus to textarea after sending message
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, 0);
  };

  const wrapperStyle = 'flex gap-2 p-4 absolute w-full';
  const inputStyle =
    'placeholder:text-base 2xl:placeholder:text-base w-full placeholder:font-inherit placeholder:text-placeholder outline-none';
  const buttonStyle =
    'flex items-center justify-center w-[34px] h-[34px] bg-primary rounded-full p-2 text-white disabled:bg-gray-400 disabled:cursor-not-allowed';

  return (
    <div
      className={cn(
        wrapperStyle,
        'sidebar-footer mt-auto sticky bottom-0 xl:rounded-br-4xl flex flex-col'
      )}
    >
      <div className="flex gap-2 items-center relative mt-auto">
        <div className="relative w-full">
          <textarea
            ref={textareaRef}
            disabled={inProgress || isPending}
            placeholder="Ask me anything..."
            className={cn(
              inputStyle,
              'resize-none overflow-y-auto max-h-[144px] pr-12',
              isThanks && '!pointer-events-none'
            )}
            rows={1}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
            }}
            onInput={(e) => {
              const target = e.currentTarget;

              target.style.height = 'auto';

              const lineHeight = parseFloat(
                getComputedStyle(target).lineHeight
              );
              const linesCount = Math.floor(target.scrollHeight / lineHeight);

              const maxLines = 3;
              const maxHeight = lineHeight * maxLines;

              if (linesCount > maxLines) {
                target.style.height = `${maxHeight}px`;
                target.style.overflowY = 'auto';
                setShowFade(true);
              } else {
                target.style.height = `${target.scrollHeight}px`;
                target.style.overflowY = 'hidden';
                setShowFade(false);
              }
              
              // Maintain focus after height adjustment
              target.focus();
            }}
            onKeyDown={async (e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                await handleSend();
              }
            }}
          />

          {showFade && (
            <div
              className="pointer-events-none absolute top-0 left-0 right-0 h-6"
              style={{
                background: 'linear-gradient(to bottom, white, transparent)',
              }}
            />
          )}
        </div>

        <button
          disabled={inProgress || isPending}
          className={cn(
            buttonStyle,
            'absolute right-0',
            text.trim().length > 0
              ? 'bottom-0 translate-y-0'
              : 'top-1/2 -translate-y-1/2',
            'cursor-pointer'
          )}
          onClick={handleSend}
        >
          {inProgress || isPending ? (
            <Loader2 className="text-white animate-spin" />
          ) : (
            <ArrowUp className="text-white" />
          )}
        </button>
      </div>
    </div>
  );
};
